import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LegalService, { BaaDocument, SIGNER_TITLE_MAX, SIGNER_TITLE_MIN } from '../../services/legal.service';
import { useAuth } from '../../context/authcontext';
import { describeApiError } from '../../utils/apiErrors';
import peloraLogo from '../../assets/images/logofullblack.png';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BAASignPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loadPermissions } = useAuth();
  const documentRef = useRef<HTMLDivElement>(null);
  const [document, setDocument] = useState<BaaDocument | null>(null);
  const [printedName, setPrintedName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [workEmail, setWorkEmail] = useState(user?.email || '');
  const [authorityAttested, setAuthorityAttested] = useState(false);
  const [hasReadToEnd, setHasReadToEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    LegalService.getCurrentBaa()
      .then(baa => {
        if (!cancelled) setDocument(baa);
      })
      .catch(err => {
        if (!cancelled) setError(describeApiError(err, 'Unable to load the BAA'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const handleDocumentScroll = () => {
    const element = documentRef.current;
    if (element) {
      setHasReadToEnd(element.scrollTop + element.clientHeight >= element.scrollHeight - 8);
    }
  };

  const trimmedTitle = signerTitle.trim();
  const titleValid = trimmedTitle.length >= SIGNER_TITLE_MIN && trimmedTitle.length <= SIGNER_TITLE_MAX;

  const canSubmit = Boolean(
    document &&
    hasReadToEnd &&
    agreed &&
    authorityAttested &&
    titleValid &&
    printedName.trim().length >= 2 &&
    EMAIL_PATTERN.test(workEmail.trim()) &&
    !isSubmitting
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!document || !canSubmit) return;

    setError(null);
    setIsSubmitting(true);
    try {
      await LegalService.acceptBaa({
        agreed_by_name: printedName.trim(),
        work_email: workEmail.trim().toLowerCase(),
        content_hash: document.content_hash,
        signer_title: trimmedTitle,
        authority_attested: true
      });
      await loadPermissions();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(describeApiError(err, 'Unable to save BAA acceptance'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 text-slate-900">
      <section className="mx-auto max-w-4xl rounded-lg bg-white p-6 shadow-sm">
          <img
            src={peloraLogo}
            alt="Pelora Health"
            className="mb-6 h-auto w-48 max-w-[70vw] object-contain"
          />
          <h1 className="text-2xl font-semibold">Business Associate Agreement</h1>
          <p className="mt-2 text-sm text-slate-600">
            Review and accept this agreement before using Pelora Health.
          </p>
          {document?.version && (
            <p className="mt-1 text-xs text-slate-500">Version {document.version}</p>
          )}

          <div
            ref={documentRef}
            onScroll={handleDocumentScroll}
            className="mt-6 h-[min(55vh,30rem)] overflow-y-auto rounded border border-slate-200 bg-slate-50 p-5 font-mono text-sm leading-6 whitespace-pre-wrap"
          >
            {isLoading ? 'Loading agreement...' : document?.content || 'The agreement could not be loaded.'}
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <label className="block text-sm font-medium">
              Full printed name
              <input
                value={printedName}
                onChange={event => setPrintedName(event.target.value)}
                disabled={isSubmitting}
                className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 font-normal"
                autoComplete="name"
                required
              />
            </label>

            <label className="block text-sm font-medium">
              Title
              <input
                value={signerTitle}
                onChange={event => setSignerTitle(event.target.value)}
                disabled={isSubmitting}
                maxLength={SIGNER_TITLE_MAX}
                placeholder="e.g. Administrator, Owner, Director of Nursing"
                className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 font-normal"
                autoComplete="organization-title"
                aria-invalid={signerTitle.length > 0 && !titleValid}
                required
              />
              {signerTitle.length > 0 && !titleValid && (
                <span className="mt-1 block text-xs font-normal text-red-700">
                  Enter a title of {SIGNER_TITLE_MIN} to {SIGNER_TITLE_MAX} characters.
                </span>
              )}
            </label>

            <label className="block text-sm font-medium">
              Work email
              <input
                type="email"
                value={workEmail}
                onChange={event => setWorkEmail(event.target.value)}
                disabled={isSubmitting}
                className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 font-normal"
                autoComplete="email"
                required
              />
            </label>

            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={agreed}
                onChange={event => setAgreed(event.target.checked)}
                disabled={isSubmitting}
                className="mt-1"
              />
              <span>I have read and agree to this Business Associate Agreement on behalf of my organization.</span>
            </label>

            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={authorityAttested}
                onChange={event => setAuthorityAttested(event.target.checked)}
                disabled={isSubmitting}
                className="mt-1"
              />
              <span>I am authorized to sign on behalf of the organization.</span>
            </label>

            {error && <p className="text-sm text-red-700" role="alert">{error}</p>}

            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isSubmitting ? 'Saving...' : 'Accept agreement'}
            </button>
          </form>
      </section>
    </main>
  );
};

export default BAASignPage;